import axios from 'axios'

export interface CurrencyRate {
  currencyCode: string
  rate: number
  date: string
}

export interface UsdPair {
  code: string
  value: number
  date: string
}

// Ответ fx-api: массив кортежей [base, quote, createdAt, sealedAt, value];
// актуальная котировка пары — последняя (sealedAt === null).
type FxRateRow = [string, string, string, string | null, number]

// Отдельный инстанс: fx-api — внешний сервис, ему не нужны baseURL
// посткалк-API и Bearer-заголовок глобального axios.
const fxHttp = axios.create()

// fx-api периодически отвечает 500 вспышками — пара ретраев с паузой
// обычно спасает загрузку страницы.
function getWithRetry(
  url: string,
  params: Record<string, string>,
  attempts: number
): Promise<{ data: FxRateRow[] }> {
  return fxHttp.get(url, { params }).catch(error => {
    if (attempts <= 1) {
      throw error
    }

    return new Promise(resolve => setTimeout(resolve, 700)).then(() =>
      getWithRetry(url, params, attempts - 1)
    )
  })
}

const CurrencyRatesService = {
  /** Расчётные курсы к рублю из БД посткалка (ими считает бэкенд). */
  getRates(): Promise<CurrencyRate[]> {
    return axios
      .get('/admin/currency-rates')
      .then(response => response.data.rates)
  },

  /** Прямые пары USD/<code> с сервиса курсов, без кросса через рубль. */
  getUsdPairs(): Promise<UsdPair[]> {
    const apiUrl = process.env.VUE_APP_FX_API_URL

    if (!apiUrl) {
      return Promise.resolve([])
    }

    const url = apiUrl.replace(/\/+$/, '') + '/rates'

    return getWithRetry(url, { baseCode: 'USD' }, 3).then(({ data }) => {
      const current: Record<string, UsdPair> = {}

      data.forEach((row: FxRateRow) => {
        const [, quote, createdAt, sealedAt, value] = row

        // История отсортирована по времени: незакрытая запись — актуальная.
        if (sealedAt === null || !current[quote]) {
          current[quote] = { code: quote, value, date: createdAt }
        }
      })

      return Object.values(current)
    })
  },
}

export default CurrencyRatesService
